import { jobToResults } from './results/jobToResults';
import type { BacktestResults } from './results/types';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import { getBacktestJobByIdQuery } from 'src/queries/backtestSpecs';
import type { ReactBindings } from 'src/types/bindings';

export type BacktestResultsState =
    | { readonly status: 'idle' }
    | { readonly status: 'loading'; readonly jobId: string }
    | { readonly status: 'loaded'; readonly jobId: string; readonly results: BacktestResults }
    | { readonly status: 'error'; readonly jobId: string; readonly message: string };

type BacktestResultsAction =
    | { type: 'LOAD_STARTED'; jobId: string }
    | { type: 'LOAD_SUCCEEDED'; jobId: string; results: BacktestResults }
    | { type: 'LOAD_FAILED'; jobId: string; message: string }
    | { type: 'CLEAR' };

function resultsReducer(state: BacktestResultsState, action: BacktestResultsAction): BacktestResultsState {
    switch (action.type) {
        case 'LOAD_STARTED':
            return { status: 'loading', jobId: action.jobId };
        case 'LOAD_SUCCEEDED':
            return { status: 'loaded', jobId: action.jobId, results: action.results };
        case 'LOAD_FAILED':
            return { status: 'error', jobId: action.jobId, message: action.message };
        case 'CLEAR':
            return { status: 'idle' };
        default:
            throw new UnreachableCaseError(action);
    }
}

/**
 * Owns the currently-viewed backtest job's results. `loadResults(jobId)` fetches
 * the full BacktestJob (including the trade array that the list endpoint omits)
 * and transforms it into display-ready `BacktestResults`. View is only offered
 * for completed jobs, so each results screen fetches exactly once.
 */
export class BacktestResultsModel implements Disposable {
    readonly #disposableStack = new DisposableStack();
    readonly #logger: ReactBindings['Logger'];
    readonly #queryClient: ReactBindings['QueryClient'];

    readonly #state: Signal<BacktestResultsState>;
    /** Epoch increments on every loadResults/clear so late resolves can be dropped. */
    #epoch = 0;

    readonly state: ReadonlySignal<BacktestResultsState>;
    readonly results: ReadonlySignal<BacktestResults | null>;
    readonly loading: ReadonlySignal<boolean>;

    constructor(logger: ReactBindings['Logger'], queryClient: ReactBindings['QueryClient']) {
        this.#logger = logger;
        this.#queryClient = queryClient;

        this.#state = signal<BacktestResultsState>({ status: 'idle' });
        this.state = this.#state;
        this.results = computed(() => (this.#state.value.status === 'loaded' ? this.#state.value.results : null));
        this.loading = computed(() => this.#state.value.status === 'loading');
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }

    #dispatch(action: BacktestResultsAction): void {
        this.#state.value = resultsReducer(this.#state.value, action);
    }

    async loadResults(jobId: string): Promise<void> {
        const epoch = ++this.#epoch;
        this.#dispatch({ type: 'LOAD_STARTED', jobId });
        try {
            const job = await this.#queryClient.fetchQuery({
                ...getBacktestJobByIdQuery(jobId),
                retry: false,
                staleTime: 0,
            });
            if (epoch !== this.#epoch) return;
            if (!job) {
                this.#dispatch({ type: 'LOAD_FAILED', jobId, message: 'Backtest job not found' });
                return;
            }
            const results = jobToResults(job);
            if (!results) {
                this.#dispatch({ type: 'LOAD_FAILED', jobId, message: 'Backtest job has no results' });
                return;
            }
            this.#dispatch({ type: 'LOAD_SUCCEEDED', jobId, results });
        } catch (e) {
            if (epoch !== this.#epoch) return;
            this.#logger.warn('Failed to load backtest job results', { jobId, error: e });
            this.#dispatch({
                type: 'LOAD_FAILED',
                jobId,
                message: 'Failed to load backtest job details. Please try again.',
            });
        }
    }

    clear(): void {
        this.#epoch++;
        if (this.#state.peek().status !== 'idle') {
            this.#dispatch({ type: 'CLEAR' });
        }
    }
}
